import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSettings } from "@/contexts/SettingsContext";
import { TrendingUp, Mail, Lock, User, Gift, Loader2, Check, Eye, EyeOff } from "lucide-react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";

function PasswordStrength({ password }) {
  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 6) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  }, [password]);

  if (!password) return null;

  const labels = ["", "Zayıf", "Orta", "İyi", "Güçlü", "Çok Güçlü"];
  const colors = ["", "var(--iq-red)", "var(--iq-orange)", "#ffd740", "var(--iq-green)", "#00e676"];
  const pct = (strength / 5) * 100;

  return (
    <div className="mt-2">
      <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--iq-border-bright)" }}>
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, background: colors[strength] }} />
      </div>
      <p className="text-[10px] mt-1" style={{ color: colors[strength] || "var(--iq-text-dim)" }}>
        {labels[strength]}
      </p>
    </div>
  );
}

export default function Register() {
  const { setUser } = useAuth();
  const { t } = useI18n();
  const { settings, loading: settingsLoading } = useSettings();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialRef = sp.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: initialRef });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => { if (initialRef) setForm((f) => ({ ...f, referral_code: initialRef })); }, [initialRef]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: form.email, password: form.password, name: form.name,
        referral_code: form.referral_code || undefined,
      });
      if (data.access_token) localStorage.setItem("iq_token", data.access_token);
      setUser(data.user);
      nav("/trade");
    } catch (er) { setError(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-app grid-lines relative">
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(800px 500px at 50% -20%, rgba(0,230,118,0.08), transparent 70%), radial-gradient(600px 400px at 20% 80%, rgba(68,138,255,0.04), transparent 60%)"
        }}
      />
      <TopBar />
      <div className="relative z-10 flex items-start sm:items-center justify-center min-h-[calc(100vh-56px)] px-4 sm:px-4 pt-8 sm:pt-8 pb-16 sm:pb-10">
        <div className="w-full max-w-md mob-page" style={{ animation: "page-in 0.3s ease-out" }}>
          <div
            className="rounded-xl p-6 sm:p-8 border"
            style={{
              background: "var(--iq-panel-glass)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              borderColor: "var(--iq-border)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.03) inset",
            }}
          >
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              {settingsLoading ? (
                <div className="w-9 h-9 rounded-lg bg-panel-hover shimmer" />
              ) : settings?.logo_base64 ? (
                <img src={settings.logo_base64} alt="Logo" className="max-h-9" />
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <TrendingUp className="w-5 h-5 text-black" />
                  </div>
                  <span className="text-xl font-bold tracking-tight">{settings?.site_title || <>IQ<span className="text-buy">Trade</span></>}</span>
                </div>
              )}
            </div>

            <h1 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight">{t("register")}</h1>
            <p className="text-sm text-muted mb-6">Ücretsiz hesap oluştur ve işlem yapmaya başla</p>

            {initialRef && (
              <div className="mb-5 flex items-center gap-2.5 bg-gradient-to-r from-buy-dim/80 to-transparent border border-buy/30 rounded-lg px-3.5 py-2.5">
                <Gift className="w-4 h-4 text-buy shrink-0" />
                <span className="text-xs text-buy">
                  <span className="font-bold font-mono">{initialRef}</span> referans kodu ile kaydoluyorsun
                </span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">{t("name")}</label>
                <div
                  className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                  style={{ background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-green)";
                    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-border-bright)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <User className="w-4 h-4 text-dim shrink-0" />
                  <input
                    data-testid="register-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ad Soyad"
                    className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">{t("email")}</label>
                <div
                  className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                  style={{ background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-green)";
                    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-border-bright)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Mail className="w-4 h-4 text-dim shrink-0" />
                  <input
                    data-testid="register-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="ornek@email.com"
                    className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">{t("password")} (min 6)</label>
                <div
                  className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                  style={{ background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-green)";
                    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(0,230,118,0.08)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-border-bright)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Lock className="w-4 h-4 text-dim shrink-0" />
                  <input
                    data-testid="register-password"
                    type={showPw ? "text" : "password"}
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                    className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((p) => !p)}
                    className="text-dim hover:text-white transition-colors shrink-0 touch-target"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrength password={form.password} />
              </div>

              {/* Referral code */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim mb-1.5 block">Referral code (optional)</label>
                <div
                  className="flex items-center gap-3 rounded-lg px-3.5 transition-all duration-200"
                  style={{ background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)" }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-blue)";
                    e.currentTarget.style.boxShadow = "0 0 0 2px rgba(68,138,255,0.08)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "var(--iq-border-bright)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <Gift className="w-4 h-4 text-dim shrink-0" />
                  <input
                    data-testid="register-referral"
                    value={form.referral_code}
                    onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                    placeholder="VARSAYILAN"
                    className="flex-1 bg-transparent outline-none text-sm py-3.5 sm:py-3 placeholder:text-dim/60 font-mono uppercase"
                  />
                </div>
              </div>

              {/* Terms */}
              <label className="flex items-start gap-2.5 cursor-pointer py-1 select-none">
                <div
                  onClick={() => setAgreeTerms((r) => !r)}
                  className="w-4.5 h-4.5 rounded flex items-center justify-center transition-all duration-150 shrink-0 mt-0.5"
                  style={{
                    width: 18,
                    height: 18,
                    background: agreeTerms ? "var(--iq-green)" : "var(--iq-bg)",
                    border: agreeTerms ? "1px solid var(--iq-green)" : "1px solid var(--iq-border-bright)",
                  }}
                >
                  {agreeTerms && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </div>
                <span className="text-xs text-muted leading-relaxed">
                  Hesap oluşturarak{" "}
                  <span className="text-buy hover:underline cursor-pointer">Kullanım Koşulları</span> ve{" "}
                  <span className="text-buy hover:underline cursor-pointer">Gizlilik Politikası</span>'nı kabul ediyorum
                </span>
              </label>

              {error && (
                <div data-testid="register-error" className="text-sm text-sell font-mono bg-sell-dim/20 border border-sell/20 rounded-lg px-3.5 py-2.5 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sell shrink-0" />
                  {error}
                </div>
              )}

              <button
                data-testid="register-submit"
                disabled={busy || !agreeTerms}
                type="submit"
                className="touch-target w-full font-bold py-3.5 sm:py-3 rounded-lg uppercase tracking-widest text-sm transition-all duration-200 disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, var(--iq-green) 0%, #00c853 100%)",
                  color: "#000",
                  boxShadow: busy ? "none" : "0 4px 20px rgba(0,230,118,0.25)",
                }}
                onMouseEnter={(e) => {
                  if (!busy) e.currentTarget.style.boxShadow = "0 6px 30px rgba(0,230,118,0.4)";
                }}
                onMouseLeave={(e) => {
                  if (!busy) e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,230,118,0.25)";
                }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("register")}
              </button>
            </form>

            <div className="mt-7 text-sm text-muted text-center border-t border-line/50 pt-6">
              {t("have_account")}{" "}
              <Link to="/login" className="text-buy hover:underline hover:text-emerald-300 transition-colors font-semibold inline-block py-1" data-testid="to-login">
                {t("login")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
